// -----------------------------------------------------------------------------
// src/pages/settings/User.jsx
// MOBILE-OPTIMIZED component with Clean Header & Integrated Tabs
// -----------------------------------------------------------------------------

import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  Tab,
  Tabs,
  Tooltip,
  Badge,
  LinearProgress,
  useTheme,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Drawer,
  Hidden,
  Stack,
  Paper,
} from "@mui/material";
import { SketchPicker } from "react-color";
import EditIcon from "@mui/icons-material/Edit";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PaletteIcon from "@mui/icons-material/Palette";
import ApiIcon from "@mui/icons-material/Api";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TeamIcon from "@mui/icons-material/Group";
import LogoutIcon from "@mui/icons-material/Logout";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PercentIcon from "@mui/icons-material/Percent";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { GoogleLogin } from "@react-oauth/google";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSearchParams, useNavigate } from "react-router-dom";

import StyledTabs from "../../Components/StyledTabs";
import TabPanel from "../../Components/TabPanel";
import DeveloperPanel from "./ApiServices/developerPanel";
import UploadFileDialog from "./Users/sepcupload";
import NotifSettings from "./Users/notif_settings";
import MyTeam from "./Users/myteam";
import useAlertDialog from "../../Components/useAlertDialog";
import AlertDialog from "../../Components/AlertDialog";

import client from "../../API";
import ppfLogo from "../../Components/globalAssets/ppfLogo.png";
import { switchRole } from "../../API/roleSwitcher";
import useAuth from "../../Hooks/use-auth";
import routes from "../../Config/routes";
import UserContractView from "../Contracts/usercontractview";
import AssignmentIcon from "@mui/icons-material/Assignment";

/* ────────────────────────────────────────────────────────────────────────── */
/* Main User Component                                                      */
/* ────────────────────────────────────────────────────────────────────────── */

const User = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = searchParams.get("tab") || "team";
  const navigate = useNavigate();
  const { unauthorize } = useAuth();
  const [pendingContractsCount, setPendingContractsCount] = useState(0);

  // Mobile responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const setTabValue = (newValue) => setSearchParams({ tab: newValue });
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  /* ───────── Mobile Navigation State ───────── */
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  /* ───────── Dialog States ───────── */
  const { dialogState, openDialog } = useAlertDialog();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [legalEntityWarningOpen, setLegalEntityWarningOpen] = useState(false);
  const [legalEntityDialogOpen, setLegalEntityDialogOpen] = useState(false);
  const [legalEntityName, setLegalEntityName] = useState("");

  /* ───────── User Profile State ───────── */
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    address: "",
    phphref: "",
    phoneNumber: "",
  });

  /* ───────── Appearance Settings ───────── */
  const [appearanceSettings, setAppearanceSettings] = useState({
    darkMode: null,
    fontPreference: "default",
    uiDensity: "normal",
    themeColors: {
      primary: prefersDarkMode ? "#cb534f" : "#7b2a2a",
      secondary: "#646aff",
    },
  });

  /* ───────── Creator Account State ───────── */
  const [creatorHandleInput, setCreatorHandleInput] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [creatorLinked, setCreatorLinked] = useState(false);
  const [linkedHandle, setLinkedHandle] = useState(null);

  /* ───────── Event Handlers ───────── */
  const handleLogout = () => {
    unauthorize();
    navigate(routes.home || "/home");
  };

  const toggleMobileNav = () => setMobileNavOpen(!mobileNavOpen);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (isMobile) {
      setMobileNavOpen(false);
    }
  };

  /* ───────── Data Queries ───────── */
  const { data: userInfo } = useQuery(["user"], client.users.fetchUser, {
    onSuccess: (data) => {
      if (!data) return;
      setUserData((prev) => ({
        ...prev,
        firstName: data.first_name || "",
        lastName: data.last_name || "",
        email: data.email || "",
        username: data.username || "",
        address: data.address || "",
        phphref: data.phphref || "",
        phoneNumber: data.phone_number || "",
      }));
    },
  });

  const {
    data: companyData,
    isLoading: companyLoading,
  } = useQuery(
    ["company", userInfo?.company_id],
    () => client.companies.listFetch({ id: userInfo?.company_id }),
    { enabled: !!userInfo?.company_id }
  );

  const {
    data: ledgerEntries,
    isLoading: ledgerLoading,
    isError: ledgerError,
    error: ledgerErrorObj,
  } = useQuery(
    ["creditLedger", userInfo?.company_id],
    () => client.creditLedger.fetch(userInfo.company_id),
    { enabled: !!userInfo?.company_id }
  );

  const { data: userSettings } = useQuery(["userSettings"], () =>
    client.users.fetchUserSettings()
  );

  const { data: teamData } = useQuery("team", client.users.fetchMyTeam);

  useQuery(["globalUser"], client.users.fetchGlobalUser, {
    onSuccess: (g) => {
      if (!g) return;
      if (g.creator_username) {
        setCreatorLinked(true);
        setLinkedHandle(g.creator_username);
      }
    },
  });

  /* ───────── Mutations ───────── */
  const saveUserMutation = useMutation(client.users.updateUser, {
    onSuccess: () => queryClient.invalidateQueries("user"),
    onError: (error) =>
      openDialog("Error", error?.response?.data?.error || error.message),
  });

  const saveSettingsMutation = useMutation(
    (payload) =>
      userSettings?.id
        ? client.users.updateUserSettings({ id: userSettings.id, ...payload })
        : client.users.createUserSettings(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["userSettings", "user"]);
      },
      onError: (error) =>
        openDialog("Error", error?.response?.data?.error || error.message),
    }
  );

  const linkCreatorMutation = useMutation(client.users.linkCreatorAccount, {
    onSuccess: (resp) => {
      setCreatorLinked(true);
      setLinkedHandle(resp.creator_username);
      queryClient.invalidateQueries("globalUser");
      openDialog("Success", "Creator account linked successfully!");
    },
    onError: (error) => {
      openDialog("Error", error?.response?.data?.error || error.message);
    },
    onSettled: () => setLinkBusy(false),
  });

  const updateLegalEntityMutation = useMutation(
    (data) => client.companies.updateLegalEntity(companyData.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["company", userInfo?.company_id]);
        setLegalEntityDialogOpen(false);
        setLegalEntityWarningOpen(false);
        setLegalEntityName("");
        openDialog("Success", "Legal entity name updated successfully!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to update legal entity");
      },
    }
  );

  /* ───────── Side Effects ───────── */
  useEffect(() => {
    if (userSettings?.id) {
      setAppearanceSettings({
        darkMode: userSettings.dark_mode,
        fontPreference: userSettings.font_preference,
        uiDensity: userSettings.ui_density,
        themeColors: userSettings.theme_colors || {
          primary: "#1976d2",
          secondary: "#9c27b0",
        },
      });
    }
  }, [userSettings]);

  useEffect(() => {
    // Show warning dialog if legal entity is not configured and data is loaded
    if (companyData && !companyData.legal_entity_name && !legalEntityWarningOpen) {
      setLegalEntityWarningOpen(true);
    }
  }, [companyData, legalEntityWarningOpen]);

  /* ───────── Form Handlers ───────── */
  const handleUserInfoChange = (e) =>
    setUserData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSaveUserInfo = async () => {
    await saveUserMutation.mutateAsync({
      first_name: userData.firstName,
      last_name: userData.lastName,
      email: userData.email,
      username: userData.username,
      address: userData.address,
      phphref: userData.phphref,
      phone_number: userData.phoneNumber,
    });
  };

  const handleSaveAppearance = async () => {
    await saveSettingsMutation.mutateAsync({
      dark_mode: appearanceSettings.darkMode,
      font_preference: appearanceSettings.fontPreference,
      ui_density: appearanceSettings.uiDensity,
      theme_colors: appearanceSettings.themeColors,
    });
  };

  const handleLinkCreator = () => {
    if (!creatorHandleInput.trim()) {
      openDialog("Error", "Please enter a creator username");
      return;
    }
    setLinkBusy(true);
    linkCreatorMutation.mutate({ creator_username: creatorHandleInput.trim() });
  };

  const handleLegalEntityUpdate = () => {
    if (!legalEntityName.trim()) {
      openDialog("Error", "Please enter a legal entity name");
      return;
    }
    updateLegalEntityMutation.mutate({ legal_entity_name: legalEntityName.trim() });
  };

  const handleConfigureLegalEntity = () => {
    setLegalEntityWarningOpen(false);
    setLegalEntityName(companyData?.legal_entity_name || "");
    setLegalEntityDialogOpen(true);
  };

  const handleGoToTeamSettings = () => {
    setLegalEntityWarningOpen(false);
    setTabValue("team");
  };

  const switchToCreatorDashboard = () => {
    switchRole("creator");
    navigate("/creatorconnect/start");
  };

  useEffect(() => {
  let active = true;
  (async () => {
    try {
      const res = await client.contracts.getMyPendingContracts();
      if (active) setPendingContractsCount(res?.pending_contracts?.length || res?.count || 0);
    } catch (e) {
      console.error("contracts badge fetch failed", e);
    }
  })();
  return () => { active = false; };
}, []);

  /* ───────── Calculate Metrics ───────── */
  const safeTeamData = Array.isArray(teamData) ? teamData : [];
  const activeTeamMembers = safeTeamData.filter(member => member.status).length;
  const totalTeamMembers = safeTeamData.length;
  const pendingApprovals = safeTeamData.filter(member => !member.status).length;
  const creditUtilization = companyData?.credit_line ? 
    ((companyData.credit_line - (companyData.balance || 0)) / companyData.credit_line * 100) : 0;

  /* ───────── Mobile Navigation Component ───────── */
  const MobileNavigation = () => (
    <Drawer
      anchor="left"
      open={mobileNavOpen}
      onClose={() => setMobileNavOpen(false)}
      PaperProps={{
        sx: { width: 280, backgroundColor: "background.paper" }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
          Settings
        </Typography>
        <List>
          {[
            { 
              value: "team", 
              icon: <Badge badgeContent={pendingApprovals || null} color="warning"><TeamIcon /></Badge>, 
              label: "Team & Credit Hub",
              primary: true
            },
            { value: "profile", icon: <PersonIcon />, label: "Your Profile" },
            { value: "appearance", icon: <PaletteIcon />, label: "Appearance" },
            { value: "notif", icon: <NotificationsIcon />, label: "Notifications" },
            { 
            value: "contracts",
            icon: (
              <Badge badgeContent={pendingContractsCount || null} color="warning">
                <AssignmentIcon />
              </Badge>
            ),
            label: "Contracts"
          },
         { value: "dev", icon: <ApiIcon />, label: "Developers" },
            
          ].map((item) => (
            <ListItem 
              key={item.value}
              button 
              onClick={() => handleTabChange(null, item.value)}
              sx={{ 
                borderRadius: 2, 
                mb: item.primary ? 1 : 0.5,
                backgroundColor: tabValue === item.value ? (item.primary ? "primary.main" : "action.selected") : "transparent",
                color: tabValue === item.value && item.primary ? "white" : "text.primary",
              }}
            >
              <ListItemIcon sx={{ color: "inherit" }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{ fontWeight: item.primary ? 600 : 400 }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );

  /* ───────── Render Component ───────── */
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1400,
        margin: "auto",
        p: isMobile ? 1 : 3,
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      <AlertDialog alertState={dialogState} />

      {/* Clean Header with Company Info & Tabs */}
      <Paper
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
          color: "white",
        }}
      >
        {/* Mobile App Bar */}
        {isMobile && (
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <IconButton
                color="inherit"
                onClick={toggleMobileNav}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                {companyData?.name || "Company Settings"}
              </Typography>
              <IconButton color="inherit" onClick={handleLogout}>
                <LogoutIcon />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Company Header Section */}
        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: isMobile ? 2 : 3, 
            mb: 3,
            flexDirection: isMobile ? "column" : "row",
            textAlign: isMobile ? "center" : "left"
          }}>
            <Avatar
              src={companyData?.phphref || ppfLogo}
              sx={{ 
                width: isMobile ? 60 : 80, 
                height: isMobile ? 60 : 80, 
                border: "3px solid rgba(255,255,255,0.8)", 
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                sx={{ fontWeight: 700, mb: 0.5 }}
              >
                {companyData?.name || "Your Company"}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9, 
                  mb: 1,
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1,
                  justifyContent: isMobile ? "center" : "flex-start"
                }}
              >
                {companyData?.legal_entity_name ? (
                  <>
                    <BusinessIcon fontSize="small" />
                    {companyData.legal_entity_name}
                  </>
                ) : (
                  <>
                    <WarningIcon 
                      fontSize="small" 
                      sx={{ color: "warning.main" }} 
                    />
                    <Box 
                      component="span" 
                      sx={{ 
                        color: "warning.main", 
                        fontWeight: 600,
                        textDecoration: "underline",
                        cursor: "pointer"
                      }}
                      onClick={() => setLegalEntityWarningOpen(true)}
                    >
                      Legal entity not configured - Click to set up
                    </Box>
                  </>
                )}
              </Typography>
              
              {/* Status Chips */}
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
                <Chip
                  icon={<TeamIcon />}
                  label={`${activeTeamMembers}/${totalTeamMembers} Active`}
                  size="small"
                  sx={{ 
                    backgroundColor: "rgba(76, 175, 80, 0.2)", 
                    color: "white", 
                    fontWeight: 600
                  }}
                />
                {pendingApprovals > 0 && (
                  <Chip
                    icon={<PendingActionsIcon />}
                    label={`${pendingApprovals} Pending`}
                    size="small"
                    sx={{ 
                      backgroundColor: "rgba(255, 152, 0, 0.2)", 
                      color: "white", 
                      fontWeight: 600
                    }}
                  />
                )}
              </Stack>
            </Box>
            
            {/* Desktop Actions */}
            {!isMobile && (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<RequestQuoteIcon />}
                  onClick={() => setCreditDialogOpen(true)}
                  sx={{ boxShadow: "0 4px 15px rgba(0,0,0,0.2)" }}
                >
                  Adjust Credit
                </Button>
                <Tooltip title="Log out">
                  <Button
                    variant="outlined"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.5)",
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Logout
                  </Button>
                </Tooltip>
              </Stack>
            )}
          </Box>

          {/* Compact Metrics */}
          <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 3 }}>
            {[
              {
                icon: <AccountBalanceWalletIcon />,
                value: `$${companyData?.balance || "0.00"}`,
                label: "Balance",
                progress: null
              },
              {
                icon: <TrendingUpIcon />,
                value: `$${companyData?.credit_line || "0"}`,
                label: "Credit Line",
                progress: creditUtilization
              },
              {
                icon: <TeamIcon />,
                value: activeTeamMembers,
                label: "Team Members",
                progress: null
              },
              {
                icon: <PercentIcon />,
                value: `${companyData?.nominal_interest_rate || "0"}%`,
                label: "Interest Rate",
                progress: null
              }
            ].map((metric, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Paper sx={{ 
                  p: isMobile ? 1 : 1.5, 
                  textAlign: "center", 
                  background: "rgba(255,255,255,0.15)", 
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "white"
                }}>
                  {React.cloneElement(metric.icon, { 
                    sx: { fontSize: isMobile ? 20 : 24, mb: 0.5, opacity: 0.8 } 
                  })}
                  <Typography 
                    variant={isMobile ? "body1" : "h6"} 
                    sx={{ fontWeight: 700, mb: 0.25 }}
                  >
                    {metric.value}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: "0.7rem" }}>
                    {metric.label}
                  </Typography>
                  {metric.progress !== null && (
                    <LinearProgress 
                      variant="determinate" 
                      value={metric.progress} 
                      sx={{ 
                        mt: 0.5, 
                        height: 3,
                        backgroundColor: "rgba(255,255,255,0.3)",
                        "& .MuiLinearProgress-bar": { backgroundColor: "rgba(255,255,255,0.8)" }
                      }} 
                    />
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Integrated Tabs */}
          {!isMobile && (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "white",
                  height: 3,
                },
                "& .MuiTab-root": {
                  color: "rgba(255,255,255,0.7)",
                  fontWeight: 600,
                  "&.Mui-selected": {
                    color: "white",
                  },
                },
              }}
            >
              <Tab 
                icon={
                  <Badge badgeContent={pendingApprovals || null} color="warning">
                    <TeamIcon />
                  </Badge>
                } 
                label="Team & Credit Hub" 
                value="team"
                iconPosition="start"
              />
              <Tab icon={<PersonIcon />} label="Profile" value="profile" iconPosition="start" />
              <Tab icon={<PaletteIcon />} label="Appearance" value="appearance" iconPosition="start" />
              <Tab icon={<NotificationsIcon />} label="Notifications" value="notif" iconPosition="start" />
              <Tab
                icon={
                  <Badge badgeContent={pendingContractsCount || null} color="warning">
                    <AssignmentIcon />
                  </Badge>
                }
                label="Contracts"
                value="contracts"
                iconPosition="start"
              />
              <Tab icon={<ApiIcon />} label="Developers" value="dev" iconPosition="start" />
            </Tabs>
          )}
        </Box>
      </Paper>

      {/* Mobile Navigation Drawer */}
      {isMobile && <MobileNavigation />}

      {/* Main Content Area */}
      <Box>
        {/* Team & Credit Hub Tab */}
        <TabPanel value={tabValue} index="team">
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}
            >
              Team & Credit Management Hub
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ mb: 3 }}
            >
              Manage your team members, credit operations, contracts, and legal documentation.
            </Typography>
            <MyTeam />
          </Box>
        </TabPanel>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index="profile">
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                sx={{ fontWeight: 700, mb: 3 }}
              >
                Profile Settings
              </Typography>
              
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <Avatar
                    src={userData.phphref || ppfLogo}
                    sx={{ 
                      width: isMobile ? 80 : 120, 
                      height: isMobile ? 80 : 120, 
                      fontSize: isMobile ? "1.5rem" : "2.5rem", 
                      mb: 2 
                    }}
                  />
                  <IconButton
                    onClick={() => setUploadDialogOpen(true)}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                      backgroundColor: "background.paper",
                      "&:hover": { backgroundColor: "action.selected" },
                      opacity: 0.8,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  sx={{ fontWeight: 700 }}
                >
                  {userData.firstName} {userData.lastName}
                </Typography>
                <Typography color="text.secondary">@{userData.username}</Typography>
              </Box>

              <Grid container spacing={isMobile ? 2 : 3}>
                {[
                  { name: "firstName", label: "First Name", xs: 12, md: 6 },
                  { name: "lastName", label: "Last Name", xs: 12, md: 6 },
                  { name: "email", label: "Email", xs: 12, md: 6, type: "email" },
                  { name: "username", label: "Username", xs: 12, md: 6 },
                ].map((f) => (
                  <Grid item xs={f.xs} md={f.md} key={f.name}>
                    <TextField
                      fullWidth
                      label={f.label}
                      type={f.type || "text"}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      value={userData[f.name]}
                      onChange={handleUserInfoChange}
                      name={f.name}
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                    multiline
                    rows={2}
                    value={userData.address}
                    onChange={handleUserInfoChange}
                    name="address"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    type="tel"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                    name="phoneNumber"
                    value={userData.phoneNumber}
                    onChange={handleUserInfoChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ mb: 1 }}
                  >
                    Add Google Account as login method
                  </Typography>
                  <Box sx={{ width: "min-content" }}>
                    <GoogleLogin
                      onSuccess={({ credential }) =>
                        client.users.updateGoogle({ google_token: credential })
                      }
                      onError={(e) => openDialog("Error", e.error)}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: isMobile ? 2 : 4 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Creator Account
                  </Typography>

                  {creatorLinked ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexDirection: isMobile ? "column" : "row",
                        p: 2,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 2,
                        backgroundColor: "action.hover",
                      }}
                    >
                      <Chip
                        icon={<LinkIcon />}
                        label={`Linked to @${linkedHandle}`}
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={switchToCreatorDashboard}
                        startIcon={<PersonIcon />}
                        fullWidth={isMobile}
                      >
                        Switch to Creator Dashboard
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: "flex", 
                      gap: 2, 
                      flexDirection: isMobile ? "column" : "row" 
                    }}>
                      <TextField
                        label="Creator Username"
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        value={creatorHandleInput}
                        onChange={(e) => setCreatorHandleInput(e.target.value)}
                        fullWidth={isMobile}
                      />
                      <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        disabled={linkBusy}
                        onClick={handleLinkCreator}
                        fullWidth={isMobile}
                      >
                        {linkBusy ? <CircularProgress size={22} /> : "Link Account"}
                      </Button>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} sx={{ textAlign: isMobile ? "center" : "right" }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSaveUserInfo}
                    disabled={saveUserMutation.isLoading}
                    fullWidth={isMobile}
                  >
                    {saveUserMutation.isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Appearance Tab */}
        <TabPanel value={tabValue} index="appearance">
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                sx={{ mb: 3, fontWeight: 700 }}
              >
                Appearance Settings
              </Typography>

              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Theme Mode"
                    value={appearanceSettings.darkMode}
                    onChange={(e) =>
                      setAppearanceSettings((prev) => ({
                        ...prev,
                        darkMode: e.target.value,
                      }))
                    }
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  >
                    <MenuItem value={null}>System Default</MenuItem>
                    <MenuItem value={true}>Dark</MenuItem>
                    <MenuItem value={false}>Light</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="UI Density"
                    value={appearanceSettings.uiDensity}
                    onChange={(e) =>
                      setAppearanceSettings((prev) => ({
                        ...prev,
                        uiDensity: e.target.value,
                      }))
                    }
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  >
                    <MenuItem value="compact">Compact</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="comfortable">Comfortable</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Font Family"
                    value={appearanceSettings.fontPreference}
                    onChange={(e) =>
                      setAppearanceSettings((prev) => ({
                        ...prev,
                        fontPreference: e.target.value,
                      }))
                    }
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  >
                    <MenuItem value="default">System Default</MenuItem>
                    <MenuItem value="roboto">Roboto</MenuItem>
                    <MenuItem value="open-sans">Open Sans</MenuItem>
                    <MenuItem value="montserrat">Montserrat</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: isMobile ? 2 : 3 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Color Customization
                  </Typography>
                  <Box sx={{ 
                    display: "flex", 
                    gap: 3, 
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "center" : "flex-start"
                  }}>
                    {["primary", "secondary"].map((key) => (
                      <Box key={key} sx={{ textAlign: "center" }}>
                        <FormHelperText sx={{ mb: 1, textAlign: "center" }}>
                          {key.charAt(0).toUpperCase() + key.slice(1)} Color
                        </FormHelperText>
                        <SketchPicker
                          color={appearanceSettings.themeColors[key]}
                          onChangeComplete={(color) =>
                            setAppearanceSettings((prev) => ({
                              ...prev,
                              themeColors: { ...prev.themeColors, [key]: color.hex },
                            }))
                          }
                          width={isMobile ? "250px" : undefined}
                        />
                      </Box>
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: isMobile ? "center" : "right" }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSaveAppearance}
                    disabled={saveSettingsMutation.isLoading}
                    fullWidth={isMobile}
                  >
                    {saveSettingsMutation.isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Save Preferences"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Other Tabs */}
        <TabPanel value={tabValue} index="dev">
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <DeveloperPanel />
            </CardContent>
          </Card>
        </TabPanel>
   <TabPanel value={tabValue} index="contracts">
  <UserContractView />
</TabPanel>


        <TabPanel value={tabValue} index="notif">
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                sx={{ mb: 3, fontWeight: 700 }}
              >
                Notification Settings
              </Typography>
              <NotifSettings />
            </CardContent>
          </Card>
        </TabPanel>
      </Box>

      {/* Legal Entity Warning Dialog */}
      <Dialog
        open={legalEntityWarningOpen}
        onClose={() => setLegalEntityWarningOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "3px solid",
            borderColor: "warning.main",
          }
        }}
      >
        <DialogTitle sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 2, 
          backgroundColor: "warning.50",
          color: "warning.dark",
          fontWeight: 700
        }}>
          <WarningIcon sx={{ fontSize: 32 }} />
          Legal Entity Configuration Required
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={() => setLegalEntityWarningOpen(false)}
            size="small"
            sx={{ color: "warning.dark" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            Your company's legal entity name has not been configured yet. This is required for:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Legal contracts and agreements</strong> - All contracts will reference this name
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Credit operations</strong> - Required for credit applications and approvals
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Financial transactions</strong> - Ensures proper business entity identification
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Regulatory compliance</strong> - Required for business registration and tax purposes
            </Typography>
          </Box>
          <Box sx={{ 
            p: 2, 
            backgroundColor: "info.50", 
            borderRadius: 2, 
            border: "1px solid", 
            borderColor: "info.200" 
          }}>
            <Typography variant="body2" color="info.dark">
              <strong>Note:</strong> This should be your official registered business name as it appears in government filings and legal documents.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setLegalEntityWarningOpen(false)}
            color="inherit"
          >
            Remind Me Later
          </Button>
          <Button
            onClick={handleGoToTeamSettings}
            variant="outlined"
            startIcon={<TeamIcon />}
          >
            Go to Team Settings
          </Button>
          <Button
            onClick={handleConfigureLegalEntity}
            variant="contained"
            startIcon={<BusinessIcon />}
            sx={{ fontWeight: 600 }}
          >
            Configure Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Legal Entity Configuration Dialog */}
      <Dialog
        open={legalEntityDialogOpen}
        onClose={() => setLegalEntityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Configure Legal Entity Name
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your company's official legal name as it appears in business registration documents.
          </Typography>
          <TextField
            fullWidth
            label="Legal Entity Name"
            value={legalEntityName}
            onChange={(e) => setLegalEntityName(e.target.value)}
            placeholder="e.g., Your Company LLC, Your Corp Inc."
            helperText="This will be used in all future contracts and legal documents"
            sx={{ mb: 2 }}
          />
          <Box sx={{ 
            p: 2, 
            backgroundColor: "grey.50", 
            borderRadius: 2, 
            border: "1px solid", 
            borderColor: "grey.200" 
          }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Examples:</strong><br />
              • "Acme Corporation Inc."<br />
              • "Smith & Associates LLC"<br />
              • "Global Tech Solutions Ltd."
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setLegalEntityDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLegalEntityUpdate}
            disabled={updateLegalEntityMutation.isLoading || !legalEntityName.trim()}
            sx={{ fontWeight: 600 }}
          >
            {updateLegalEntityMutation.isLoading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Save Legal Entity"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit Dialog */}
      <CreditDialog
        open={creditDialogOpen}
        onClose={() => setCreditDialogOpen(false)}
        ledgerEntries={ledgerEntries}
        ledgerLoading={ledgerLoading}
        ledgerError={ledgerError}
        ledgerErrorObj={ledgerErrorObj}
        companyId={userInfo?.company_id}
        isMobile={isMobile}
      />

      {/* Upload Dialog */}
      <UploadFileDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={(fileData) =>
          setUserData((prev) => ({ ...prev, phphref: fileData.url }))
        }
      />
    </Box>
  );
};

/* ────────────────────────────────────────────────────────────────────────── */
/* Credit Dialog Component                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

const CreditDialog = ({
  open,
  onClose,
  ledgerEntries,
  ledgerLoading,
  ledgerError,
  ledgerErrorObj,
  companyId,
  isMobile = false,
}) => {
  const queryClient = useQueryClient();
  const [requestedCreditLine, setRequestedCreditLine] = useState("");
  const [requestedInterestRate, setRequestedInterestRate] = useState("");
  const [reason, setReason] = useState("");

  const postCreditRequestMutation = useMutation(
    (input) => client.creditLedger.postRequest(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["creditLedger", companyId]);
        onClose();
        setReason("");
        setRequestedCreditLine("");
        setRequestedInterestRate("");
      },
      onError: (error) =>
        alert(error?.response?.data?.error || error.message),
    }
  );

  const handleSubmit = () => {
    postCreditRequestMutation.mutate({
      company_id: companyId,
      requested_credit_line: Number(requestedCreditLine),
      requested_interest_rate: Number(requestedInterestRate),
      reason: reason || "User request",
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullScreen={isMobile}
      PaperProps={{ 
        sx: { 
          borderRadius: isMobile ? 0 : 3, 
          width: "100%", 
          maxWidth: isMobile ? "100%" : 1200,
          margin: isMobile ? 0 : undefined,
          height: isMobile ? "100%" : "auto"
        } 
      }}
    >
      <DialogTitle sx={{ 
        fontSize: isMobile ? "1.25rem" : "1.5rem",
        p: isMobile ? 2 : 3
      }}>
        Adjust Credit & Interest Rate
      </DialogTitle>
      <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
        <DialogContentText sx={{ mb: 2, fontSize: isMobile ? "0.875rem" : undefined }}>
          Below is the historical credit ledger for your company. You can also request a change to your credit line and/or nominal interest rate.
        </DialogContentText>

        {ledgerLoading && <CircularProgress size={24} sx={{ m: 2 }} />}
        {ledgerError && (
          <Typography color="error" sx={{ fontSize: isMobile ? "0.875rem" : undefined }}>
            Error loading ledger: {ledgerErrorObj?.message}
          </Typography>
        )}
        {ledgerEntries?.length > 0 && (
          <Box sx={{ 
            maxHeight: isMobile ? 150 : 200, 
            overflowY: "auto", 
            mb: 2, 
            border: "1px solid #ccc", 
            borderRadius: 1, 
            p: 1 
          }}>
            {ledgerEntries.map((entry) => (
              <Box key={entry.id} sx={{ mb: 1, p: 1, borderBottom: "1px solid #eee" }}>
                <Typography variant={isMobile ? "caption" : "body2"}>
                  <strong>{entry.operation_type.toUpperCase()}</strong> on{" "}
                  {new Date(entry.created_at).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: isMobile ? "0.7rem" : undefined }}>
                  Change: {entry.change_amount} | New Credit: {entry.new_credit_line} |{" "}
                  New Rate: {entry.new_interest_rate}%
                </Typography>
                {entry.reason && (
                  <Typography 
                    variant="caption" 
                    display="block"
                    sx={{ fontSize: isMobile ? "0.7rem" : undefined }}
                  >
                    Reason: {entry.reason}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Grid container spacing={isMobile ? 2 : 2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Requested Credit Line"
              type="number"
              fullWidth
              size={isMobile ? "small" : "medium"}
              value={requestedCreditLine}
              onChange={(e) => setRequestedCreditLine(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Requested Interest Rate"
              type="number"
              fullWidth
              size={isMobile ? "small" : "medium"}
              value={requestedInterestRate}
              onChange={(e) => setRequestedInterestRate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Reason for Change"
              fullWidth
              multiline
              rows={2}
              size={isMobile ? "small" : "medium"}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ 
        p: isMobile ? 2 : 3,
        flexDirection: isMobile ? "column-reverse" : "row",
        gap: isMobile ? 1 : 0
      }}>
        <Button 
          onClick={onClose}
          fullWidth={isMobile}
          size={isMobile ? "large" : "medium"}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit} 
          disabled={postCreditRequestMutation.isLoading}
          fullWidth={isMobile}
          size={isMobile ? "large" : "medium"}
        >
          {postCreditRequestMutation.isLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Submit Request"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default User;